import { useState, useEffect } from "react"
import {
  X,
  Info,
  Package,
  Calendar,
  User,
  Phone,
  MapPin,
  CreditCard,
  Truck,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  ShoppingBag,
  Clock
} from "lucide-react"
import axios from "axios"
import { format } from "date-fns"
import { showToast } from "../utils/toastUtils"

const BASE_URL = process.env.REACT_APP_API_URL;

const ProductInfoModal = ({ isOpen, onClose, product }) => {
  if (!isOpen || !product) return null

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-600" />
            Product Details
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 hover:bg-gray-100 p-1 rounded-full transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50">
            <span className="text-sm text-gray-500">Item No</span>
            <span className="text-sm font-semibold text-gray-900">{product.itemNo}</span>
          </div>
          <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50">
            <span className="text-sm text-gray-500">Base Code</span>
            <span className="text-sm font-mono font-medium text-blue-600">{product.baseCode}</span>
          </div>
          <div className="flex justify-between items-center p-2 rounded-lg bg-gray-50">
            <span className="text-sm text-gray-500">Type</span>
            <span className="text-sm font-medium text-gray-900">{product.productType}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const OrderDetailsModal = ({ isOpen, onClose, orderId, token, onOrderUpdated }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [orderDetails, setOrderDetails] = useState(null)
  const [globalThreshold, setGlobalThreshold] = useState(2000)
  const [defaultShippingCost, setDefaultShippingCost] = useState(350)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [productInfoModalOpen, setProductInfoModalOpen] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmationChecked, setConfirmationChecked] = useState(false)

  if (token) {
    axios.defaults.headers.common["x-auth-token"] = token
  }

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId || !isOpen) return
      try {
        setLoading(true)
        const [res, thresholdRes, shippingRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/orders/${orderId}`),
          axios.get(`${BASE_URL}/api/settings/freeDeliveryThreshold`).catch(() => null),
          axios.get(`${BASE_URL}/api/settings/defaultShippingCost`).catch(() => null)
        ])
        setOrderDetails(res.data)
        
        if (thresholdRes && thresholdRes.data && thresholdRes.data.value) {
          setGlobalThreshold(parseFloat(thresholdRes.data.value))
        }
        if (shippingRes && shippingRes.data && shippingRes.data.value) {
          setDefaultShippingCost(parseFloat(shippingRes.data.value))
        }

        setError(null)
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching order details")
      } finally {
        setLoading(false)
      }
    }
    fetchOrderDetails()
  }, [orderId, isOpen])

  const handleProductClick = async (productId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/products`)
      const product = res.data.find((p) => p._id === productId)
      setSelectedProduct(product)
      setProductInfoModalOpen(true)
    } catch (err) {
      console.error("Failed to load product info", err)
    }
  }

  const formatAddress = (order) => {
    if (!order) return ""
    const parts = [
      order.addressLine1,
      order.addressLine2,
      order.addressLine3,
      order.city,
      order.zipCode,
      order.province
    ].filter(Boolean)
    return parts.join(", ")
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case "New": return { color: "text-amber-700 bg-amber-50 border-amber-200", icon: Clock }
      case "Pending Packed": return { color: "text-blue-700 bg-blue-50 border-blue-200", icon: Package }
      case "Packed": return { color: "text-indigo-700 bg-indigo-50 border-indigo-200", icon: CheckCircle }
      case "Handover to Courier": return { color: "text-cyan-700 bg-cyan-50 border-cyan-200", icon: Truck }
      case "Delivered": return { color: "text-green-700 bg-green-50 border-green-200", icon: CheckCircle }
      case "Cancelled": return { color: "text-red-700 bg-red-50 border-red-200", icon: X }
      case "Returned": return { color: "text-rose-700 bg-rose-50 border-rose-200", icon: X }
      default: return { color: "text-gray-700 bg-gray-50 border-gray-200", icon: Info }
    }
  }

  if (!isOpen) return null

  const statusConfig = orderDetails ? getStatusConfig(orderDetails.order.status) : {}
  const StatusIcon = statusConfig.icon || Info

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
        <div
          className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">

          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
                Order Details
                {orderDetails && (
                  <span className="text-sm font-medium text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-md ml-2">
                    #{orderDetails.order.orderId}
                  </span>
                )}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="overflow-y-auto p-6 md:p-8 custom-scrollbar space-y-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
                <p className="text-gray-500 font-medium">Loading details...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {error}
              </div>
            ) : orderDetails ? (
              <>
                {/* Top Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Order Information Card */}
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-4">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2 border-b border-gray-200 pb-2">
                      <Info className="w-4 h-4 text-blue-500" /> Order Info
                    </h4>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Date
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {format(new Date(orderDetails.order.createdAt), "MMM d, yyyy, h:mm a")}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Status
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border flex items-center gap-1.5 ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {orderDetails.order.status}
                        </span>
                      </div>

                      {orderDetails.order.courierServiceId?.name && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <Truck className="w-4 h-4" /> Courier
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {orderDetails.order.courierServiceId.name}
                          </span>
                        </div>
                      )}

                      {orderDetails.order.trackingNumber && (
                        <div className="flex justify-between items-start">
                          <span className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                            <ExternalLink className="w-4 h-4" /> Tracking
                          </span>
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs font-mono bg-white border border-gray-200 px-2 py-1 rounded-md text-gray-700 select-all">
                              {orderDetails.order.trackingNumber}
                            </span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${orderDetails.order.trackingStatus === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {orderDetails.order.trackingStatus || "Status Pending"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Information Card */}
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-4">
                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2 border-b border-gray-200 pb-2">
                      <User className="w-4 h-4 text-purple-500" /> Customer Info
                    </h4>

                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                          <User className="w-4 h-4" /> Name
                        </span>
                        <span className="text-sm font-medium text-gray-900 text-right">
                          {orderDetails.order.customerName}
                        </span>
                      </div>

                      <div className="flex justify-between items-start">
                        <span className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                          <Phone className="w-4 h-4" /> Phone
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-sm font-medium text-gray-900">{orderDetails.order.phone}</span>
                          {orderDetails.order.phone2 && (
                            <span className="text-xs text-gray-500">{orderDetails.order.phone2}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-between items-start">
                        <span className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                          <MapPin className="w-4 h-4" /> Address
                        </span>
                        <span className="text-sm text-gray-700 text-right max-w-[200px] leading-relaxed">
                          {formatAddress(orderDetails.order)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" /> Payment
                        </span>
                        {orderDetails.order.paymentMethod === 'cod' ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full border border-green-200">
                            Cash On Delivery
                          </span>
                        ) : (
                          <span className="text-sm font-medium capitalize text-gray-900">
                            {orderDetails.order.paymentMethod || "standard"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items Table */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-500" /> Order Items
                  </h4>
                  <div className="bg-white border boundary-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50/50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Variation</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Qty</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Price (Unit)</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {orderDetails.orderLines.map((line) => (
                          <tr key={line._id} className="hover:bg-blue-50/30 transition-colors">
                            <td className="px-6 py-4">
                              <button
                                onClick={() => handleProductClick(line.product)}
                                className="group flex flex-col items-start gap-1 text-left"
                              >
                                <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors flex items-center gap-1.5">
                                  {line.productName}
                                  <Info className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </span>
                                <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                  {line.productType}
                                </span>
                              </button>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {line.variation || "-"}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-700 text-sm font-bold">
                                {line.quantity}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right text-sm text-gray-600">
                              Rs. {(line.lineTotal / line.quantity).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                              Rs. {line.lineTotal.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="flex justify-end">
                  <div className="w-full max-w-sm bg-gray-50 rounded-xl p-6 border border-gray-100 space-y-3">
                    {(() => {
                      const subtotal = orderDetails.order.subTotal || 0
                      const shippingCost = orderDetails.order.shippingCost !== undefined
                        ? orderDetails.order.shippingCost
                        : (subtotal >= globalThreshold ? 0 : (orderDetails.order.originalShippingCost || defaultShippingCost))

                      const total = orderDetails.order.total || (subtotal + shippingCost)

                      return (
                        <>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Subtotal</span>
                            <span className="font-medium text-gray-900">Rs. {subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Shipping</span>
                            {shippingCost === 0 ? (
                              <span className="text-green-600 font-medium text-xs bg-green-50 px-2 py-0.5 rounded-full">Free Shipping</span>
                            ) : (
                              <span className="font-medium text-gray-900">Rs. {shippingCost.toFixed(2)}</span>
                            )}
                          </div>
                          <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                            <span className="text-base font-bold text-gray-900">Total Amount</span>
                            <span className="text-xl font-bold text-blue-600">Rs. {total.toFixed(2)}</span>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>

                {/* Confirm Action for New Orders */}
                {orderDetails.order.status === "New" && !orderDetails.order.trackingNumber && (
                  <div className="flex items-center justify-end pt-4 border-t border-gray-100 mt-4">
                    <button
                      onClick={() => {
                        setShowConfirmModal(true)
                        setConfirmationChecked(false)
                      }}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all transform hover:-translate-y-0.5"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Confirm & Generate Tracking
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No order details found.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirmModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Generate Tracking?</h2>
              <p className="text-sm text-gray-500 mt-2 max-w-xs">
                This will revert status to <strong>Pending Packed</strong> and generate a waybill number. This action cannot be easily reversed.
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 text-left">
                  I understand that this will generate a tracking number through the courier API.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={!confirmationChecked}
                onClick={async () => {
                  try {
                    const res = await axios.post(`${BASE_URL}/api/orders/${orderDetails.order._id}/track-fardar`)
                    const trackingNumber = res.data.waybill_no

                    setOrderDetails({
                      ...orderDetails,
                      order: {
                        ...orderDetails.order,
                        trackingNumber,
                        status: "Pending Packed",
                      },
                    })
                    setShowConfirmModal(false)
                    if (typeof onOrderUpdated === "function") {
                      onOrderUpdated(orderDetails.order._id)
                    }
                    onClose()
                    showToast("Success", "Tracking generated successfully", "success")
                  } catch (err) {
                    const errorMessage = err.response?.data?.message || "Failed to generate tracking number"
                    showToast("Error", errorMessage, "error")
                    console.error(err)
                  }
                }}
                className={`flex-1 px-4 py-2.5 font-medium rounded-xl text-white transition-all shadow-md ${confirmationChecked
                  ? "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20"
                  : "bg-gray-300 cursor-not-allowed shadow-none"
                  }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductInfoModal
        isOpen={productInfoModalOpen}
        onClose={() => setProductInfoModalOpen(false)}
        product={selectedProduct}
      />
    </>
  )
}

export default OrderDetailsModal

